import { DataTable } from "@/components/Base/Datatable";
import { useEffect, useState } from "react";
import { Skeleton } from "@/components/Base/Skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { usePaginatedShops } from "@/hooks/queries/usePaginatedShops";
import { useTotalShopsQuery } from "@/hooks/queries/useTotalShopsQuery";
import { DatatableShop, shopColumns } from "@/components/Shops/Shops.columns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/Base/Tooltip";
import { Button } from "@/components/Base/Button";
import { IconNames } from "@/types/v2";
import { Icon } from "@/components/Common/Icon";

export const ShopsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get values from URL params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
  const sortBy = searchParams.get("sortBy") ?? "createdAt"; // Default sort by createdAt
  const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") ?? "desc"; // Default sort order is descending

  const { data, isLoading } = usePaginatedShops({
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
  });

  const { data: totalShops } = useTotalShopsQuery();

  // Helper function to get sorting from params
  const getSortingFromParams = (): SortingState => {
    if (sortBy && sortOrder) {
      return [{ id: sortBy, desc: sortOrder === "desc" }];
    }
    return [];
  };

  // Initialize state from URL params
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>(getSortingFromParams());

  // Update pagination state when URL changes (browser back/forward)
  useEffect(() => {
    const newPageIndex = currentPage - 1;
    const newPageSize = pageSize;

    // Only update if values actually changed
    if (
      pagination.pageIndex !== newPageIndex ||
      pagination.pageSize !== newPageSize
    ) {
      setPagination({
        pageIndex: newPageIndex,
        pageSize: newPageSize,
      });
    }
  }, [currentPage, pageSize]); // Remove pagination from dependencies

  // Update sorting state when URL changes
  useEffect(() => {
    const newSorting = getSortingFromParams();

    // Only update if sorting actually changed
    const sortingChanged =
      sorting.length !== newSorting.length ||
      (newSorting.length > 0 &&
        (sorting.length === 0 ||
          sorting[0].id !== newSorting[0].id ||
          sorting[0].desc !== newSorting[0].desc));

    if (sortingChanged) {
      setSorting(newSorting);
    }
  }, [sortBy, sortOrder]); // Use specific params instead of searchParams

  // Update URL when pagination changes (but only from user interaction)
  useEffect(() => {
    const urlPage = currentPage;
    const urlPageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const actualPage = pagination.pageIndex + 1;

    // Only update URL if pagination state differs from URL state
    if (actualPage !== urlPage || pagination.pageSize !== urlPageSize) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", actualPage.toString());
      newParams.set("pageSize", pagination.pageSize.toString());
      setSearchParams(newParams);
    }
  }, [pagination.pageIndex, pagination.pageSize]); // Remove searchParams from dependencies

  // Handle sorting changes and reset pagination
  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newSorting =
      typeof updaterOrValue === "function"
        ? updaterOrValue(sorting)
        : updaterOrValue;

    setSorting(newSorting);

    // Update URL with new sorting and reset to page 1
    const newParams = new URLSearchParams(searchParams);

    if (newSorting.length > 0) {
      const sort = newSorting[0];
      newParams.set("sortBy", sort.id);
      newParams.set("sortOrder", sort.desc ? "desc" : "asc");
    } else {
      newParams.delete("sortBy");
      newParams.delete("sortOrder");
    }

    // Reset to page 1 when sorting changes
    newParams.set("page", "1");
    setSearchParams(newParams);

    // Update local pagination state immediately to prevent flicker
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const actions = (shop: DatatableShop) => {
    return [
      // Edit button - available for all employees
      <TooltipProvider key={"update"}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => navigate(shop.id)}
              variant="ghost"
              id="edit_employee"
            >
              <Icon name={IconNames.open} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>View this shop&#39;s details.</TooltipContent>
        </Tooltip>
      </TooltipProvider>,
    ];
  };

  if (!data || isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-12 w-24 self-end" />
        <Skeleton className="h-24 w-full" />
        <div className="flex flex-row justify-end gap-4">
          <Skeleton className="h-12 w-24" />
          <Skeleton className="h-12 w-24" />
        </div>
      </div>
    );
  }

  return (
    <>
      <DataTable
        // Data is based on the data list, with pagination applied
        data={data?.docs}
        columns={shopColumns({
          actions: actions,
        })}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={handleSortingChange}
        rowCount={totalShops}
        loading={isLoading}
        userDefaults={{
          columnVisibility: {
            // Show the ID column by default
            id: true,
          },
        }}
      />
    </>
  );
};
